import React, { useState, useEffect, useCallback, useRef } from 'react'
import { tokens } from '@/design-system/tokens'
import { search, SearchResult } from '@/lib/api'
import { Search, FileText, Users, Receipt, CheckSquare, HelpCircle, ShoppingCart, File, MessageCircle, ClipboardList, X, Stamp, GitBranch, Package } from "lucide-react"
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/atoms/Button'
import { Input } from '@/components/atoms/Input'

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
  /** TASK-DETAIL-PEEK-SURFACE-SEARCH — called for task/decision results instead of navigate */
  onOpenPeek?: (id: string, kind: 'task' | 'decision') => void
}

/** Returns true if `id` looks like a UUID (app_tasks use UUIDs; old tasks use int strings). */
function isUUID(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
}

const typeIcons: Record<string, React.ReactNode> = {
  project:        <FileText size={16} />,
  contact:        <Users size={16} />,
  invoice:        <Receipt size={16} />,
  task:           <CheckSquare size={16} />,
  rfi:            <HelpCircle size={16} />,
  purchase_order: <ShoppingCart size={16} />,
  document:       <File size={16} />,
  message:        <MessageCircle size={16} />,
  app_task:       <ClipboardList size={16} />,
  // PERMITS-REDESIGN-SURFACE-SWEEP — permit search results
  permit:         <Stamp size={16} />,
  // TASK-DETAIL-PEEK-SURFACE-SEARCH — decision results (schedule_type from app_tasks)
  decision:       <GitBranch size={16} />,
  milestone:      <CheckSquare size={16} />,
  phase:          <ClipboardList size={16} />,
  // SPEC-SHOPPING-LIST Stage 4 — procurement tracker + shopping run search results
  procurement_item: <Package size={16} />,
  shopping_run:      <ShoppingCart size={16} />,
  // DL-SURFACES-SWEEP-2 — daily/project log search results
  daily_log:         <ClipboardList size={16} />,
}

const typeColors: Record<string, string> = {
  project:        'var(--st-info)',
  contact:        'var(--st-blocked)',
  invoice:        'var(--st-success)',
  task:           'var(--st-warning)',
  rfi:            'var(--st-error)',
  purchase_order: 'var(--st-warning)',
  document:       'var(--st-active)',
  message:        'var(--st-active)',
  app_task:       'var(--st-success)',
  // PERMITS-REDESIGN-SURFACE-SWEEP
  permit:         'var(--brand-gold)',
  // TASK-DETAIL-PEEK-SURFACE-SEARCH
  decision:       'var(--st-warning)',
  milestone:      'var(--brand-gold)',
  phase:          'var(--st-active)',
  // SPEC-SHOPPING-LIST Stage 4
  procurement_item: 'var(--st-warning)',
  shopping_run:      'var(--st-info)',
  // DL-SURFACES-SWEEP-2
  daily_log:         'var(--st-active)',
}

export function SearchModal({ isOpen, onClose, onOpenPeek }: SearchModalProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setResults([])
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await search.query(query)
        setResults(res.results || [])
        setSelectedIndex(0)
      } catch {
        setResults([])
      }
      setLoading(false)
    }, 200)
    return () => clearTimeout(timer)
  }, [query])

  const handleSelect = useCallback((result: SearchResult) => {
    // TASK-DETAIL-PEEK-SURFACE-SEARCH — open peek inline for task/decision app_tasks
    // (UUID check guards against old `tasks` table rows which use int IDs)
    if ((result.type === 'task' || result.type === 'decision') && isUUID(result.id) && onOpenPeek) {
      onOpenPeek(result.id, result.type as 'task' | 'decision')
      onClose()
      return
    }
    onClose()
    navigate(result.url)
  }, [onClose, navigate, onOpenPeek])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex])
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '15vh',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: tokens.color.background.surface,
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${tokens.color.border.default}` }}>
          <Search size={18} style={{ color: tokens.color.text.muted, marginRight: 12 }} />
          <Input
            ref={inputRef}
            value={query}
            onChange={e => setQuery((e as any).target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search projects, contacts, invoices..."
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 16,
              color: tokens.color.text.primary,
              fontFamily: tokens.typography.fontFamily.sans,
            }}
          />
          <Button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={18} style={{ color: tokens.color.text.muted }} />
          </Button>
        </div>

        {query.trim() && (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {loading ? (
              <div style={{ padding: 20, textAlign: 'center', color: tokens.color.text.muted }}>
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: tokens.color.text.muted }}>
                No results found
              </div>
            ) : (
              results.map((result, idx) => (
                <div
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleSelect(result)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 20px',
                    cursor: 'pointer',
                    background: idx === selectedIndex ? tokens.color.background.elevated : 'transparent',
                    borderBottom: `1px solid ${tokens.color.border.subtle}`,
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${typeColors[result.type]}20`,
                      color: typeColors[result.type],
                    }}
                  >
                    {typeIcons[result.type]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: tokens.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {result.title}
                    </div>
                    <div style={{ fontSize: 12, color: tokens.color.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {result.subtitle}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color: typeColors[result.type],
                      letterSpacing: '0.05em',
                    }}
                  >
                    {result.type}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${tokens.color.border.default}`, display: 'flex', gap: 16, fontSize: 11, color: tokens.color.text.muted }}>
          <span>↑↓ Navigate</span>
          <span>↵ Select</span>
          <span>Esc Close</span>
          {query && results.length > 0 && (
            <Button
              onClick={() => { onClose(); navigate('/search') }}
              style={{ marginLeft: 'auto', color: 'var(--brand-gold)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
            >
              View all results
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
